import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useIssuePaymentCorrectionMutation,
  useRemovePaymentCorrectionMutation,
  useMarkAgreementTransferOutMutation,
  StudentDetailDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IIssueCorrectionArgs {
  agreementId: string;
  /** Omit to correct the whole agreement / plan; set to reverse one payment. */
  paymentId?: string | null;
  amount: number;
  currency: string;
  date: string;
  reason: string;
}

interface IUsePaymentCorrectionsHook {
  issue: (args: IIssueCorrectionArgs) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  setTransferOut: (agreementId: string, value: boolean) => Promise<boolean>;
  loading: boolean;
}

/**
 * Payment corrections (корректировки) — issue/remove a separate negative line
 * against an agreement's payments, and flip the manual `transferOut` flag.
 * Toasts + a StudentDetail refetch so the card/history repaint. Server gates
 * (caps, full-zeroing needs transferOut, Admin/Finance only) surface as
 * customErrors.
 */
export const usePaymentCorrections = (
  studentId?: string,
): IUsePaymentCorrectionsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const refetchQueries = useMemo(
    () =>
      studentId
        ? [{ query: StudentDetailDocument, variables: { id: studentId } }]
        : [],
    [studentId],
  );
  const opts = { refetchQueries, awaitRefetchQueries: true };

  const [issueMut, issueState] = useIssuePaymentCorrectionMutation(opts);
  const [removeMut, removeState] = useRemovePaymentCorrectionMutation(opts);
  const [flagMut, flagState] = useMarkAgreementTransferOutMutation(opts);

  const notifyError = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('pages.studentDetail.correction.failed'),
          text,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast, t],
  );

  const errText = (
    errors?: Array<{
      message?: string | null;
      code?: string | null;
    } | null> | null,
  ) =>
    errors
      ?.map((e) => e?.message || e?.code || null)
      .filter(Boolean)
      .join('\n') || undefined;

  const issue = useCallback(
    async (args: IIssueCorrectionArgs) => {
      try {
        const res = await issueMut({
          variables: {
            input: {
              agreementId: args.agreementId,
              paymentId: args.paymentId || undefined,
              amount: args.amount,
              currency: args.currency,
              date: args.date,
              reason: args.reason,
            },
          },
        });
        const p = res.data?.createPaymentCorrection;
        if (p?.success) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [issueMut, notifyError],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        const res = await removeMut({ variables: { id } });
        const p = res.data?.deletePaymentCorrection;
        if (p?.success) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [removeMut, notifyError],
  );

  const setTransferOut = useCallback(
    async (agreementId: string, value: boolean) => {
      try {
        const res = await flagMut({
          variables: { agreementId, transferOut: value },
        });
        const p = res.data?.setAgreementTransferOut;
        if (p?.agreement) return true;
        notifyError(errText(p?.customErrors));
        return false;
      } catch (e) {
        notifyError((e as Error).message);
        return false;
      }
    },
    [flagMut, notifyError],
  );

  return {
    issue,
    remove,
    setTransferOut,
    loading: issueState.loading || removeState.loading || flagState.loading,
  };
};
